import React, { useCallback, useMemo, useRef } from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import useStyles from './FindingItemType.styles';
import { Icon } from '../../../../../common/components/icons';
import priorityEnum from '../../../../models/priorityEnum';
import { FindingShape } from '../../../../models/Finding';
import { Select } from '../../../../../common/components/inputs';
import findingTypeEnum from '../../../../models/findingTypeEnum';
import { useAbilityContext } from '../../../../../common/contexts/abilityContext';

const FindingItemType = ({ patrolId, finding, onFindingTypeChange, updatedFindingType, isLoading, disabled }) => {
  const classes = useStyles({ creationMode: finding.creationMode });

  const { current: options } = useRef([
    {
      value: priorityEnum.immediate,
      renderLabel() {
        return (
          <>
            <Icon id="typeIcon" className={classes.immediateIcon} icon="warning" size="small" />
            <span className={classes.priorityLabel}>{i18n.t('finding.type.immediate')}</span>
          </>
        );
      },
      renderValue() {
        return <Icon id="typeIcon" className={classes.immediateIcon} icon="warning" size="small" />;
      },
    },
    {
      value: priorityEnum.investigation,
      renderLabel() {
        return (
          <>
            <Icon id="typeIcon" className={classes.investigateIcon} icon="error" size="small" />
            <span className={classes.priorityLabel}>{i18n.t('finding.type.investigation')}</span>
          </>
        );
      },
      renderValue() {
        return <Icon id="typeIcon" className={classes.investigateIcon} icon="error" size="small" />;
      },
    },
    {
      value: findingTypeEnum.note,
      renderLabel() {
        return (
          <>
            <Icon id="typeIcon" className={classes.noteIcon} icon="edit" size="small" />
            <span className={classes.priorityLabel}>{finding.creationMode ? i18n.t('finding.type.noteAutomatic') : i18n.t('finding.type.noteManual')}</span>
          </>
        );
      },
      renderValue() {
        return <Icon id="typeIcon" className={classes.noteIcon} icon="edit" size="small" />;
      },
    },
    {
      value: findingTypeEnum.dismissed,
      renderLabel() {
        return (
          <>
            <Icon id="typeIcon" className={classes.dismissedIcon} icon="cancel" size="small" />
            <span className={classes.priorityLabel}>{i18n.t('finding.type.dismissed')}</span>
          </>
        );
      },
      renderValue() {
        return <Icon id="typeIcon" className={classes.dismissedIcon} icon="cancel" size="small" />;
      },
    },
    {
      value: findingTypeEnum.unclassified,
      renderLabel() {
        return (
          <>
            <Icon id="typeIcon" className={classes.unclassifiedIcon} icon="help" size="small" />
            <span className={classes.priorityLabel}>{i18n.t('finding.type.unclassified')}</span>
          </>
        );
      },
      renderValue() {
        return <Icon id="typeIcon" className={classes.unclassifiedIcon} icon="help" size="small" />;
      },
    },
  ]);

  const handlePriorityChange = useCallback(
    (newType) => {
      let type;
      let priority;

      if (Object.values(priorityEnum).includes(newType)) {
        type = findingTypeEnum.threat;
        priority = newType;
      } else {
        type = newType;
      }

      onFindingTypeChange(patrolId, finding.id, { patrolId, id: finding.id, type, priority });
    },
    [finding.id, patrolId, onFindingTypeChange]
  );

  const value = useMemo(() => {
    const type = updatedFindingType && updatedFindingType.type ? updatedFindingType.type : finding.type;
    if (type === findingTypeEnum.threat) return updatedFindingType && updatedFindingType.priority ? updatedFindingType.priority : finding.priority;

    return type;
  }, [finding.priority, finding.type, updatedFindingType]);

  const context = useAbilityContext();

  return (
    <Select disabled={disabled || context.cannot('update', 'finding')} value={value} onChange={handlePriorityChange} options={options} loading={isLoading} />
  );
};

FindingItemType.propTypes = {
  disabled: PropTypes.bool,
  finding: FindingShape.isRequired,
  isLoading: PropTypes.bool,
  onFindingTypeChange: PropTypes.func.isRequired,
  patrolId: PropTypes.number.isRequired,
  updatedFindingType: FindingShape,
};

FindingItemType.defaultProps = {
  disabled: false,
  isLoading: false,
  updatedFindingType: undefined,
};

export default FindingItemType;
